import {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
  useRef,
} from "react";
import type {
  RealtimeChannel,
  RealtimePostgresInsertPayload,
} from "@supabase/supabase-js";
import { authClient } from "@/integrations/supabase/authClient";
import { useToast } from "@/hooks/use-toast";
import { doorSessionAction } from "./doorSessionAction";

interface DoorEventRow {
  event_type: string;
  auth_method: string;
  user_id: string;
}

/** The ~5s full-screen "recognised — door opens" confirmation after a face
 * entry scan starts a session. */
export interface DoorWelcome {
  name: string;
}

/** The ~6s full-screen "recognised, but session didn't start" screen after a
 * face entry scan fails to open a session — reuses the door-welcome maroon
 * treatment so the member gets the same prominence as a successful scan. */
export interface DoorDenied {
  message: string;
  cta?: { label: string; href: string };
}

// start_session's error strings, translated into member-facing (lowercase,
// brand-voice) copy explaining why the session didn't start, plus a relevant
// next step. SessionTimerProvider sits outside BrowserRouter (it wraps the
// whole app, including anonymous marketing pages), so the CTA is a plain
// href rather than a react-router navigate().
function doorDeniedContent(error?: string): DoorDenied {
  switch (error) {
    case "Room is currently booked":
      return {
        message: "that room's booked by someone else right now.",
        cta: { label: "find another time", href: "/book" },
      };
    case "Insufficient credits":
      return {
        message: "you're out of credits — top up to start a session.",
        cta: { label: "buy credits", href: "/wallet" },
      };
    // No off-peak case: peak is answered with a charge now, not a closed door.
    // A member whose membership doesn't cover the hour comes in and pays the
    // drop-in rate for the peak minutes, so there is nothing to refuse.
    default:
      return {
        message:
          "we couldn't start your session — please check in with the team.",
      };
  }
}

interface SessionTimerContextType {
  isActive: boolean;
  elapsedSeconds: number;
  sessionId: string | null;
  /** Server-anchored session start, or null when no session is active. */
  startedAt: Date | null;
  isUnlimited: boolean;
  /** Set for ~5s after a face-entry scan starts a session; null otherwise. */
  doorWelcome: DoorWelcome | null;
  dismissDoorWelcome: () => void;
  /** Set for ~6s after a face-entry scan fails to start a session; null otherwise. */
  doorDenied: DoorDenied | null;
  dismissDoorDenied: () => void;
  startTimer: () => Promise<{ success: boolean; error?: string }>;
  /**
   * End the active session. `reason` records how it ended:
   *   "manual" — the member tapped "leave room" (default)
   *   "face"   — a face-entry exit scan auto-ended it
   * The reason is stored against the session so the admin panel can flag
   * members who manually stop their own bookings.
   */
  stopTimer: (reason?: "manual" | "face") => Promise<{
    creditsDeducted: number;
    creditsRefunded?: number;
    message?: string;
    success: boolean;
    error?: string;
  }>;
  resetTimer: () => void;
}

const SessionTimerContext = createContext<SessionTimerContextType | undefined>(
  undefined,
);

/* Cadence for the while-INACTIVE poll that watches for a door-started
   session. Exported so the rule can be tested without standing up the whole
   provider. See the effect that uses it for why it is not a flat interval. */
export const INACTIVE_POLL_FAST_MS = 4_000;
export const INACTIVE_POLL_SLOW_MS = 30_000;
/** How long after arrival becomes plausible the fast cadence is kept. */
export const INACTIVE_POLL_FAST_WINDOW_MS = 60_000;

/** How long to wait before the next check, given when the fast window ends. */
export function nextInactivePollDelayMs(
  now: number,
  fastUntil: number,
): number {
  return now < fastUntil ? INACTIVE_POLL_FAST_MS : INACTIVE_POLL_SLOW_MS;
}

export function SessionTimerProvider({ children }: { children: ReactNode }) {
  const [isActive, setIsActive] = useState(false);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [serverStartTime, setServerStartTime] = useState<Date | null>(null);
  const [isUnlimited, setIsUnlimited] = useState(false);
  const [doorWelcome, setDoorWelcome] = useState<DoorWelcome | null>(null);
  const doorWelcomeTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const dismissDoorWelcome = () => {
    if (doorWelcomeTimeout.current) clearTimeout(doorWelcomeTimeout.current);
    doorWelcomeTimeout.current = null;
    setDoorWelcome(null);
  };
  const [doorDenied, setDoorDenied] = useState<DoorDenied | null>(null);
  const doorDeniedTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const dismissDoorDenied = () => {
    if (doorDeniedTimeout.current) clearTimeout(doorDeniedTimeout.current);
    doorDeniedTimeout.current = null;
    setDoorDenied(null);
  };
  const { toast } = useToast();

  // The signed-in member's id, kept in sync with auth state (not just read
  // once at mount) — SessionTimerProvider wraps the whole app above the
  // router and never remounts on login, so the two effects below that key
  // off "am I signed in" (the active-session catch-up and the door-events
  // subscription) must react to sign-in happening after this provider is
  // already mounted, not just to how things stood when it first rendered.
  const [userId, setUserId] = useState<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    authClient.getUser().then(({ data }) => {
      if (!cancelled) setUserId(data.user?.id ?? null);
    });
    const {
      data: { subscription },
    } = authClient.onAuthStateChange((_event, session) => {
      const nextId = session?.user?.id ?? null;
      setUserId(nextId);
      if (!nextId) {
        // Signed out — drop any timer state from the previous member so it
        // can't bleed into whoever uses this device/browser next.
        stateGen.current += 1;
        setIsActive(false);
        setSessionId(null);
        setServerStartTime(null);
        setElapsedSeconds(0);
        setIsUnlimited(false);
      }
    });
    return () => {
      cancelled = true;
      subscription.unsubscribe();
    };
  }, []);

  // Sessions END on the server (exit face scan / auto-close), so when one we
  // were tracking disappears, look up its final row and broadcast it — the
  // Today screen turns this into the post-session summary card.
  const announceSessionEnd = async (endedSessionId: string) => {
    try {
      const { supabase } = await import("@/integrations/supabase/client");
      const { data } = await supabase
        .from("active_sessions")
        .select("id, started_at, ended_at, credits_deducted")
        .eq("id", endedSessionId)
        .maybeSingle();
      if (!data) return;
      window.dispatchEvent(
        new CustomEvent("zz-session-ended", {
          detail: {
            sessionId: data.id,
            startedAt: data.started_at,
            endedAt: data.ended_at,
            creditsDeducted: data.credits_deducted ?? 0,
          },
        }),
      );
    } catch {
      /* summary is best-effort — never block session state on it */
    }
  };

  // Re-sync session state from the server. Sessions are started by the door
  // face scanner, NOT the app, so the realtime door event below is the only
  // push signal — and if it's missed (backgrounded app, dropped socket) the
  // member stands in the studio while the app still shows the home screen.
  // This runs on mount, when the tab becomes visible, and on a slow poll
  // while inactive, so the app always converges on the truth.
  const sessionIdRef = useRef<string | null>(null);
  useEffect(() => {
    sessionIdRef.current = sessionId;
  }, [sessionId]);

  // Which local state a server answer may still overwrite. Every local write
  // (a door scan starting the session, a stop, a reset) bumps it, and a sync
  // that was already in flight when that happened throws its answer away.
  // Without this, a get_active_session issued by the inactive poll a moment
  // BEFORE the door started the session comes back "no session" a moment
  // AFTER the app showed the timer — and, finding sessionIdRef set, ends the
  // session it never knew about: the in-session screen flashes up and falls
  // straight back to "your face is the key". Older data never wins.
  const stateGen = useRef(0);
  const bumpStateGen = () => {
    stateGen.current += 1;
  };

  const syncActiveSession = async (withSubscription = false) => {
    const gen = stateGen.current;
    const {
      data: { user },
    } = await authClient.getUser();
    if (!user) return;
    const { supabase } = await import("@/integrations/supabase/client");

    const sessionResult = await supabase.rpc("get_active_session", {
      p_user_id: user.id,
    });

    if (sessionResult.error) {
      console.error("Error checking active session:", sessionResult.error);
      return;
    }
    // Something local moved while this was on the wire; the answer describes
    // a state that no longer exists. The next tick asks again.
    if (gen !== stateGen.current) return;

    const result = sessionResult.data as {
      has_session: boolean;
      session_id?: string;
      started_at?: string;
      elapsed_seconds?: number;
    };

    if (result?.has_session && result.session_id) {
      setSessionId(result.session_id);
      setIsActive(true);
      setServerStartTime(new Date(result.started_at!));
      setElapsedSeconds(Math.max(0, result.elapsed_seconds || 0));
    } else if (sessionIdRef.current) {
      // The session we were showing ended on the server (exit scan or
      // auto-close) — announce it for the summary, then clear.
      const ended = sessionIdRef.current;
      setIsActive(false);
      setSessionId(null);
      setServerStartTime(null);
      setElapsedSeconds(0);
      void announceSessionEnd(ended);
    }

    if (withSubscription) {
      const subResult = await supabase.rpc("get_active_subscription", {
        p_user_id: user.id,
      });
      const subData = subResult.data as { has_subscription?: boolean } | null;
      setIsUnlimited(subData?.has_subscription === true);
    }
  };
  const syncRef = useRef(syncActiveSession);
  useEffect(() => {
    syncRef.current = syncActiveSession;
  });

  // Check for an existing active session as soon as a member is signed in —
  // on first mount already logged in, AND on signing in later in the same
  // app session (userId flips from null to an id without a remount). Without
  // this keying off userId, a member who opens the app logged out and then
  // logs in never gets this check at all: the door's face scan already
  // started their session server-side, but the app would sit on the home
  // screen until something else (a manual refresh) happened to catch it up.
  useEffect(() => {
    if (userId) syncRef.current(true);
  }, [userId]);

  // While INACTIVE, poll for a door-started session and re-check the moment
  // the app is foregrounded — walking in with the phone in a pocket is the
  // normal case, so the push path alone is not enough.
  //
  // Fast at first, then slow. Four seconds is right for the "I scanned my
  // face, why isn't the timer showing yet" window, but this effect does not
  // only run in that window: it runs for every signed-in member who is not
  // currently in a session, which for an app left open in a pocket is all
  // day. At 4s that is ~900 calls an hour each, all of them get_active_session
  // against the database, for an event that is not coming.
  //
  // So the fast cadence is kept for the minute after arrival becomes
  // plausible — mount, or the app being brought to the foreground — and drops
  // to 30s after that. Nothing is lost: a door-started session also arrives on
  // the realtime channel (see the door-events subscription below), and
  // foregrounding re-checks immediately and restarts the fast window, which is
  // exactly the moment a member who has just walked in looks at their phone.
  useEffect(() => {
    if (isActive) return;

    let fastUntil = Date.now() + INACTIVE_POLL_FAST_WINDOW_MS;
    let timer: ReturnType<typeof setTimeout>;

    const tick = () => {
      syncRef.current();
      timer = setTimeout(tick, nextInactivePollDelayMs(Date.now(), fastUntil));
    };
    timer = setTimeout(tick, INACTIVE_POLL_FAST_MS);

    const onVisible = () => {
      if (document.visibilityState !== "visible") return;
      // Foregrounding is the strongest signal that someone has just arrived,
      // so check now and go back to the fast cadence.
      fastUntil = Date.now() + INACTIVE_POLL_FAST_WINDOW_MS;
      syncRef.current();
      clearTimeout(timer);
      timer = setTimeout(tick, INACTIVE_POLL_FAST_MS);
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      clearTimeout(timer);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [isActive]);

  // Update elapsed seconds based on server start time
  useEffect(() => {
    let interval: NodeJS.Timeout | null = null;

    if (isActive && serverStartTime) {
      interval = setInterval(() => {
        const now = new Date();
        // Clamp to 0: a booked-room timer is anchored to the booked start, which
        // can be a few minutes in the future if the member arrives early.
        const elapsed = Math.max(
          0,
          Math.floor((now.getTime() - serverStartTime.getTime()) / 1000),
        );
        setElapsedSeconds(elapsed);
      }, 1000);
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isActive, serverStartTime]);

  // While ACTIVE, poll once a minute so an exit face scan (or the server's
  // auto-close) that the realtime channel missed still ends the session in
  // the app promptly — syncActiveSession announces the end for the summary.
  useEffect(() => {
    if (!isActive || !sessionId) return;
    const interval = setInterval(() => syncRef.current(), 60 * 1000);
    return () => clearInterval(interval);
  }, [isActive, sessionId]);

  const startTimer = async (): Promise<{
    success: boolean;
    error?: string;
  }> => {
    const {
      data: { user },
    } = await authClient.getUser();

    if (!user) {
      return { success: false, error: "Not authenticated" };
    }
    const { supabase } = await import("@/integrations/supabase/client");

    // Call server-side function to start session
    const { data, error } = await supabase.rpc("start_session", {
      p_user_id: user.id,
    });

    if (error) {
      console.error("Error starting session:", error);
      return { success: false, error: error.message };
    }

    const result = data as {
      success: boolean;
      session_id?: string;
      started_at?: string;
      already_active?: boolean;
      error?: string;
    };

    if (!result.success) {
      return { success: false, error: result.error };
    }

    // Session started successfully on server — also refresh subscription status.
    // Use the server's started_at: for a private-room booking this is the booked
    // start time (not now), so the timer reflects the time the member reserved.
    const startedAt = result.started_at
      ? new Date(result.started_at)
      : new Date();
    bumpStateGen();
    setSessionId(result.session_id!);
    setIsActive(true);
    setServerStartTime(startedAt);
    setElapsedSeconds(
      Math.max(0, Math.floor((Date.now() - startedAt.getTime()) / 1000)),
    );
    // The door usually opens the session before the app hears about it, and
    // start_session then hands back the one in progress WITHOUT its start
    // (already_active). Anchoring the ring to "now" would show a member who
    // walked in at 11:30 a timer that began when they looked at their phone;
    // pull the real start from the server instead.
    if (result.already_active && !result.started_at) void syncActiveSession();

    supabase
      .rpc("get_active_subscription", { p_user_id: user.id })
      .then(({ data }) => {
        const sub = data as { has_subscription?: boolean } | null;
        setIsUnlimited(sub?.has_subscription === true);
      });

    return { success: true };
  };

  const stopTimer = async (
    reason: "manual" | "face" = "manual",
  ): Promise<{
    creditsDeducted: number;
    creditsRefunded?: number;
    message?: string;
    success: boolean;
    error?: string;
  }> => {
    if (!sessionId) {
      return { creditsDeducted: 0, success: false, error: "No active session" };
    }

    const {
      data: { user },
    } = await authClient.getUser();

    if (!user) {
      return { creditsDeducted: 0, success: false, error: "Not authenticated" };
    }
    const { supabase } = await import("@/integrations/supabase/client");

    // Call server-side function to end session and deduct credits.
    // p_end_reason lets the server tag a manual "leave room" tap apart from a
    // Face-entry exit scan, so the admin panel can count members who stop manually.
    const { data, error } = await supabase.rpc(
      "end_session_and_deduct_credits",
      {
        p_session_id: sessionId,
        p_user_id: user.id,
        p_end_reason: reason,
      },
    );

    if (error) {
      console.error("Error ending session:", error);
      return { creditsDeducted: 0, success: false, error: error.message };
    }

    const result = data as {
      success: boolean;
      credits_deducted?: number;
      credits_refunded?: number;
      message?: string;
      error?: string;
    };

    // Reset timer state regardless of result
    const endedSessionId = sessionId;
    bumpStateGen();
    setIsActive(false);
    setSessionId(null);
    setServerStartTime(null);
    setElapsedSeconds(0);

    // The door closes the session server-side the moment the exit scan lands,
    // so the app's call often arrives second and finds nothing to close. That
    // is the normal path now, not an error — announce the end so the member
    // still gets their summary, read from the session the door already closed.
    if (!result.success) {
      if (result.error === "Session not found") {
        if (endedSessionId) void announceSessionEnd(endedSessionId);
        return { creditsDeducted: 0, success: true };
      }
      return { creditsDeducted: 0, success: false, error: result.error };
    }

    if (endedSessionId) void announceSessionEnd(endedSessionId);

    return {
      creditsDeducted: result.credits_deducted || 0,
      creditsRefunded: result.credits_refunded || 0,
      message: result.message ?? undefined,
      success: true,
    };
  };

  const resetTimer = () => {
    bumpStateGen();
    setElapsedSeconds(0);
    setIsActive(false);
    setSessionId(null);
    setServerStartTime(null);
    setIsUnlimited(false);
  };

  // ── Face-entry door automation ───────────────────────────────────────────
  // Keep the latest active-state and timer handlers in refs so the realtime
  // subscription (set up once) always calls the current versions without
  // resubscribing on every render.
  const isActiveRef = useRef(isActive);
  const startTimerRef = useRef(startTimer);
  const stopTimerRef = useRef(stopTimer);
  const memberFirstNameRef = useRef("there");
  useEffect(() => {
    isActiveRef.current = isActive;
    startTimerRef.current = startTimer;
    stopTimerRef.current = stopTimer;
  });

  // Subscribe to this member's door scans. An entry scan auto-starts a billable
  // session (start_session tags it with any current booking, so booked vs
  // walk-in is differentiated and never double-charged); an exit ends it,
  // triggering the time-based charge or the early-leave refund.
  useEffect(() => {
    // Re-runs whenever userId changes — including going from null to a real
    // id when a member signs in after this provider already mounted. Without
    // this, the channel below is only ever created for whoever was signed in
    // (or not) at the app's first render, so a member who logs in mid-session
    // never gets a door-events subscription at all.
    if (!userId) return;

    let channel: RealtimeChannel | null = null;
    let removeChannel: ((ch: RealtimeChannel) => void) | null = null;
    let cancelled = false;

    const handleDoorEvent = async (
      payload: RealtimePostgresInsertPayload<DoorEventRow>,
    ) => {
      const action = doorSessionAction(payload.new, isActiveRef.current);

      if (action === "start") {
        const res = await startTimerRef.current();
        if (res.success) {
          if (doorWelcomeTimeout.current) {
            clearTimeout(doorWelcomeTimeout.current);
          }
          setDoorWelcome({ name: memberFirstNameRef.current });
          doorWelcomeTimeout.current = setTimeout(() => {
            setDoorWelcome(null);
            doorWelcomeTimeout.current = null;
          }, 5000);
        } else if (res.error) {
          if (doorDeniedTimeout.current) {
            clearTimeout(doorDeniedTimeout.current);
          }
          setDoorDenied(doorDeniedContent(res.error));
          doorDeniedTimeout.current = setTimeout(() => {
            setDoorDenied(null);
            doorDeniedTimeout.current = null;
          }, 8000);
        }
      } else if (action === "stop") {
        // Face-entry exit scan — tag it so it isn't counted as a manual stop.
        const res = await stopTimerRef.current("face");
        if (res.success) {
          toast({
            title: "Session ended",
            description: res.message ?? "Thanks for visiting.",
          });
        }
      }
    };

    const setup = async () => {
      const {
        data: { user },
      } = await authClient.getUser();
      if (!user || cancelled) return;
      const meta = (user.user_metadata || {}) as {
        full_name?: string;
        first_name?: string;
      };
      const fullName = meta.full_name || meta.first_name || "";
      if (fullName) memberFirstNameRef.current = fullName.split(/\s+/)[0];
      const { supabase } = await import("@/integrations/supabase/client");
      if (cancelled) return;
      removeChannel = (ch) => supabase.removeChannel(ch);

      channel = supabase
        .channel(`door-events-${user.id}`)
        .on(
          "postgres_changes",
          {
            event: "INSERT",
            schema: "public",
            table: "door_events",
            filter: `user_id=eq.${user.id}`,
          },
          handleDoorEvent,
        )
        .subscribe();
    };

    setup();

    return () => {
      cancelled = true;
      if (channel && removeChannel) removeChannel(channel);
      if (doorWelcomeTimeout.current) clearTimeout(doorWelcomeTimeout.current);
      if (doorDeniedTimeout.current) clearTimeout(doorDeniedTimeout.current);
    };
  }, [userId, toast]);

  return (
    <SessionTimerContext.Provider
      value={{
        isActive,
        elapsedSeconds,
        sessionId,
        doorWelcome,
        dismissDoorWelcome,
        doorDenied,
        dismissDoorDenied,
        startedAt: serverStartTime,
        isUnlimited,
        startTimer,
        stopTimer,
        resetTimer,
      }}
    >
      {children}
      {doorWelcome && (
        <div
          className="zz-door-welcome sc-shell"
          role="status"
          onClick={dismissDoorWelcome}
        >
          <div className="zz-door-welcome-tick">
            <svg width="36" height="36" viewBox="0 0 24 24" fill="none">
              <path
                d="M4 12.5L9 17.5L20 6.5"
                stroke="currentColor"
                strokeWidth="2.4"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
          <h2>
            hello {doorWelcome.name}. <em>door's open.</em>
          </h2>
          <div className="foot">
            the clock starts counting the moment you step in
          </div>
        </div>
      )}
      {doorDenied && (
        <div
          className="zz-door-welcome zz-door-welcome--denied sc-shell"
          role="status"
          onClick={dismissDoorDenied}
        >
          <div className="zz-door-welcome-tick">
            <svg width="36" height="36" viewBox="0 0 24 24" fill="none">
              <path
                d="M6 6L18 18M18 6L6 18"
                stroke="currentColor"
                strokeWidth="2.4"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
          <h2>
            hi, <em>your session didn't start.</em>
          </h2>
          <div className="foot">{doorDenied.message}</div>
          {doorDenied.cta && (
            <a
              className="zz-door-btn"
              href={doorDenied.cta.href}
              onClick={(e) => e.stopPropagation()}
            >
              {doorDenied.cta.label}
            </a>
          )}
        </div>
      )}
    </SessionTimerContext.Provider>
  );
}

export function useSessionTimer() {
  const context = useContext(SessionTimerContext);
  if (context === undefined) {
    throw new Error(
      "useSessionTimer must be used within a SessionTimerProvider",
    );
  }
  return context;
}
